import { createContext, useContext, useMemo, useCallback, useReducer } from 'react';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import Box from '@zenbusiness/zui/Box';
import SharedAsset from '@zenbusiness/zui/SharedAsset';

import Toast from '#client/components/Toast';
import {
  COMMON_ERROR_MESSAGE_DESC,
  COMMON_ERROR_MESSAGE_TITLE,
  MFA_REQUIRED_ERROR,
  MFA_REQUIRED_TITLE
} from '#client/constants/messages';
import useBankingPath from '#client/hooks/useBankingPath';

const LOGOUT_STATUS_CODES = [401, 403];

export const ToastContext = createContext();
ToastContext.displayName = 'ToastContext';

const successIcon = (
  <SharedAsset
    alt=""
    src="/icon/micro/check-circle-green.svg"
  />
);
const errorIcon = (
  <Box width="17px">
    <SharedAsset
      alt=""
      color="#E53535"
      src="/s2/icon/warning-sign-fill.svg"
      width={34}
    />
  </Box>
);

const useToastContext = () => {
  const context = useContext(ToastContext);

  if (typeof context === 'undefined') {
    throw new Error('useToastContext must be used within a ToastContext.Provider');
  }

  return context;
};

export function ToastProvider({ children }) {
  const { login } = useAuthentication();

  const [toasts, dispatch] = useReducer((state, action) => {
    switch (action.type) {
      case 'add': {
        // check if the toast already exists
        const toastExists = state.some((toast) => toast.id === action.payload.id);
        if (toastExists) {
          return state;
        }

        return [...state, action.payload];
      }
      case 'remove':
        return [...state.filter((toast) => toast.id !== action.payload)];
      case 'clear':
        return [];
      default:
        return state;
    }
  }, []);

  const createToast = useCallback(
    ({ type, title, description, primaryAction, secondaryAction }) => {
      const id = `${type ?? ''}-${title ?? ''}-${description ?? ''}`;
      const icon = type === 'alert' ? errorIcon : successIcon;
      const toast = {
        id,
        title,
        description,
        icon,
        primaryAction,
        secondaryAction,
        type
      };
      dispatch({ type: 'add', payload: toast });
    },
    []
  );

  const clearAllToast = useCallback(() => {
    dispatch({ type: 'clear' });
  }, []);

  const bankingPath = useBankingPath();

  const createErrorToast = useCallback(
    ({ title, description, primaryAction, secondaryAction, error }) => {
      let message = description;
      let internalTitle = title;

      const code = error?.errorCode ?? error?.extensions?.code;
      if (code?.toUpperCase() === 'MFAREQUIRED') {
        const mfaStatus = error?.extensions?.mfaStatus;
        if (mfaStatus?.isEnrolled && mfaStatus?.isEnabled) {
          return login(`${bankingPath}/dashboard`);
        }

        message = MFA_REQUIRED_ERROR;
        internalTitle = MFA_REQUIRED_TITLE;
      }

      return createToast({
        type: 'alert',
        title: internalTitle,
        description: message,
        primaryAction,
        secondaryAction
      });
    },
    [createToast, login, bankingPath]
  );

  const createSuccessToast = useCallback(
    ({ title, description, primaryAction, secondaryAction }) => {
      createToast({
        type: 'action',
        title,
        description,
        primaryAction,
        secondaryAction
      });
    },
    [createToast]
  );

  const createCommonErrorToast = useCallback(
    ({ error } = {}) => {
      const httpStatusCode = error?.httpError?.status;
      const ignoreToastMessage = LOGOUT_STATUS_CODES.includes(httpStatusCode);
      if (!ignoreToastMessage) {
        createErrorToast({
          title: COMMON_ERROR_MESSAGE_TITLE,
          description: COMMON_ERROR_MESSAGE_DESC,
          error
        });
      }
    },
    [createErrorToast]
  );

  const value = useMemo(
    () => ({
      clearAllToast,
      createCommonErrorToast,
      createErrorToast,
      createSuccessToast
    }),
    [clearAllToast, createCommonErrorToast, createErrorToast, createSuccessToast]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toasts.map(({ id, type, title, description, icon, primaryAction, secondaryAction }) => (
        <Toast
          description={description}
          handleClose={() => dispatch({ type: 'remove', payload: id })}
          icon={icon}
          key={id}
          open
          primaryAction={primaryAction}
          secondaryAction={secondaryAction}
          title={title}
          variant={type}
        />
      ))}
    </ToastContext.Provider>
  );
}

export default useToastContext;
